import * as personService from '@api/person';
import { AppBarTitle } from '@components/app-bar-title';
import { createContextQuick } from '@components/quick-context';
import { RiSearchFill } from '@icon/react-icons';
import { QueryParamsAdapter, useAtomicTrigger, useCompanyMaster, useMountedGetter } from '@lib/common';
import { isSinglePartyDeal } from '@lib/company-mapping';
import { IconButton } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { filter, map } from 'rxjs/operators';
import { columns } from "../columns";
import { SentencesSubRow } from '../sentence-sub-rows';
import { EnhancedTable } from '../table/mtable';

export const [RefreshCounterProvider, useRefreshCounter] =
  createContextQuick<{
    refreshCounter: number;
    forceRefresh: () => any;
  }>();

export default function AdvancedCompanyPersonRelationSearch() {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const { loading, data, trigger: fetchData, pageCount, rowCount } = useServerData();
  const { master: services, loading: loadingServicesFromMaster } = useCompanyMaster('services'); // to call master api during rendering  of company search page
  const location = useLocation();

  const initial: any = useMemo(() => {
    if (!location.search) {
      return null;
    }
    const params = QueryParamsAdapter.fromParams(location.search);
    if (params && params.sortBy)
      (params.sortBy as Array<Object>).forEach(
        x => (x['desc'] = x['desc'] === 'false' ? false : true)
      );
    // return params;
    return {
      ...params,
      ...(params.pageIndex ? { pageIndex: +params.pageIndex } : {}),
      ...(params.pageSize ? { pageSize: +params.pageSize } : {}),
    };
  }, []);

  useEffect(() => {
    // Reset the initial pagination if it was set
    // by query params
    if (initial) {
      initial.pageSize = 10;
      initial.pageIndex = 0;
    }
  }, []);

  return (
    <>
      <AppBarTitle>
        <IconButton color="inherit">
          <RiSearchFill />
        </IconButton>{' '}
        Company Person Relations Search
      </AppBarTitle>
      <RefreshCounterProvider
        refreshCounter={refreshCounter}
        forceRefresh={() => setRefreshCounter(counter => counter + 1)}>
        <EnhancedTable
          loading={loading}
          getSubRows={SentencesSubRow}
          pageCount={pageCount}
          rowCount={rowCount}
          initial={initial}
          columns={columns}
          updateData={fetchData}
          data={data}
          getRowProps={colorMultiPartyRows}
        />
      </RefreshCounterProvider>
    </>
  );
}

const colorMultiPartyRows = row =>
  !isSinglePartyDeal(row.original) ? { style: { background: grey[100] } } : {};

function useServerData() {
  const history = useHistory();

  // Ignore api responses after the
  // component is unmounted.
  const isMounted = useMountedGetter();

  // Create a triggerable request.
  const {
    loading,
    error,
    result: { count = 0, data = [], page_size = 25 } = {},
    trigger,
  } = useAtomicTrigger(({ sortBy, pageSize, pageIndex, filters }, goToPage) => {
    // Update Location.
    const nextState = { sortBy, pageSize, pageIndex, filters };
    const defaultState = { sortBy: [], pageSize: 25, pageIndex: 0, filters: [] };
    // Filter out defaults from reflecting in url.
    const state = Object.fromEntries(
      Object.entries(nextState).filter(([k, v]) => !isEqual(defaultState[k], v))
    );

    const shouldClear = !Object.keys(state).length;

    history.replace({
      search: shouldClear ? '' : '?' + QueryParamsAdapter.toParams(state).toString(),
    });

    return personService.filter({ sortBy, pageSize, page: pageIndex + 1, filters }).pipe(
      filter(d => {
        if (d.data && d.data.count && !d.data.data.length) {
          // This happens when the data is avaliable for the given filter or
          // sort but the pagingation is beyond limits of the data.
          // To fix this, we retry the request with new data for the first page.
          goToPage(0);
          return false;
        }
        return true;
      }),
      map(d => d.data),
      filter(isMounted)
    );
  });

  return {
    loading,
    error,
    rowCount: count,
    pageCount: Math.ceil(count / page_size),
    data,
    trigger,
  } as const;
}
